import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { DateRange } from 'react-day-picker';
import { WZ_STATUSES, type WzStatus } from '@ip/shared-types';

/**
 * URL-persisted filters for the WZ list (WzList), sibling of useSalesFilters:
 * filters live in query params so navigating to the editor and back (or a
 * refresh) restores them exactly.
 *
 * The one WZ-specific rule: with NO date params in the URL the list shows the
 * current month (spec: "lista domyślnie za bieżący miesiąc"), so `from`/`to`
 * fall back to dayjs().startOf('month')/endOf('month') — never undefined at
 * rest, which also keeps the DateRangePicker showing the active default range.
 */

const VALID_STATUS = new Set<string>(WZ_STATUSES);

export interface WzFilters {
  /** issueDate lower bound (YYYY-MM-DD) — defaults to current month start. */
  from: string;
  /** issueDate upper bound (YYYY-MM-DD) — defaults to current month end. */
  to: string;
  contractorId: string | undefined;
  status: WzStatus | undefined;
  search: string;
  page: number;
}

export interface WzFilterActions {
  setDateRange: (value: DateRange | undefined) => void;
  setContractorId: (value: string | undefined) => void;
  setStatus: (value: WzStatus | undefined) => void;
  setSearch: (value: string) => void;
  setPage: (value: number) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
}

export function useWzFilters(): WzFilters & WzFilterActions {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<WzFilters>(() => {
    const df = searchParams.get('df');
    const dt = searchParams.get('dt');
    const cid = searchParams.get('cid');
    const st = searchParams.get('st');
    const q = searchParams.get('q');
    const p = searchParams.get('p');

    // Default to the current month only when the URL carries no date param at
    // all — a user-set range (even a one-sided one) always wins.
    const hasDateParam = df !== null || dt !== null;
    const from = df ?? (hasDateParam ? '' : dayjs().startOf('month').format('YYYY-MM-DD'));
    const to = dt ?? (hasDateParam ? '' : dayjs().endOf('month').format('YYYY-MM-DD'));

    return {
      from,
      to,
      contractorId: cid || undefined,
      status: st && VALID_STATUS.has(st) ? (st as WzStatus) : undefined,
      search: q || '',
      page: p ? Math.max(1, parseInt(p, 10) || 1) : 1,
    };
  }, [searchParams]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Every filter setter clears the page param — a narrowed result set starts
  // at page 1.
  const setDateRange = useCallback(
    (value: DateRange | undefined) => {
      updateParams({
        df: value?.from ? dayjs(value.from).format('YYYY-MM-DD') : null,
        dt: value?.to ? dayjs(value.to).format('YYYY-MM-DD') : null,
        p: null,
      });
    },
    [updateParams],
  );

  const setContractorId = useCallback(
    (value: string | undefined) => {
      updateParams({ cid: value || null, p: null });
    },
    [updateParams],
  );

  const setStatus = useCallback(
    (value: WzStatus | undefined) => {
      updateParams({ st: value || null, p: null });
    },
    [updateParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      updateParams({ q: value || null, p: null });
    },
    [updateParams],
  );

  const setPage = useCallback(
    (value: number) => {
      updateParams({ p: value <= 1 ? null : String(value) });
    },
    [updateParams],
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Active = user deviated from the resting defaults (current month, no
  // status/contractor/search). A present df/dt means the date range was
  // touched, so the default-month fallback no longer applies.
  const hasActiveFilters = useMemo(
    () =>
      searchParams.get('df') !== null ||
      searchParams.get('dt') !== null ||
      filters.contractorId !== undefined ||
      filters.status !== undefined ||
      filters.search !== '',
    [searchParams, filters],
  );

  return {
    ...filters,
    setDateRange,
    setContractorId,
    setStatus,
    setSearch,
    setPage,
    clearAllFilters,
    hasActiveFilters,
  };
}
